import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"
import { LocationCorrectionDialog } from "@/components/locations/location-correction-dialog"
import { LocationReferencesGallery } from "@/components/locations/location-references-gallery"
import { LocationRoomViewerDialog } from "@/components/locations/location-room-viewer-dialog"
import { Button } from "@/components/ui/primitives"
import { useInitialLoadReporter } from "@/components/shot-panel/use-initial-load-reporter"
import {
  ShotLocationPicker,
  findLocationByVariantId,
  normalizeShotLocationFacing,
  type ShotLocationPickerRow,
} from "@/components/shot-panel/shot-location-picker"
import { useShotPanelWorkspace } from "@/hooks/use-shot-panel-workspace"
import { useTranslation } from "@/hooks/use-translation"
import { patchShotLocationVariant } from "@/lib/inertia-actions"
import { locationReferenceBusy, variantReferenceBusy } from "@/lib/location-reference-jobs"
import {
  addLocationViewpoint,
  facingGenerationBlockReason,
  generateLocationReference,
  LOCATION_CARDINAL_FACINGS,
  LOCATION_PANORAMA_FACING,
  panoramaGenerationBlockReason,
  PRIMARY_SHOT_LOCATION_FACING,
  removeLocationViewpoint,
  updateLocationViewpoint,
  type LocationCardinalFacing,
  type LocationFacing,
  type LocationFacingPrereqReason,
  type LocationReferenceVersionRow,
  type LocationViewpointRow,
} from "@/lib/locations-api"
import { useReloadOnJobFinish } from "@/lib/shot-jobs"
import { confirmDelayKey } from "@/lib/confirm-delay-registry"
import { fetchShotWorkspaceDetail, fetchProjectLocations } from "@/lib/workspace-api"
import { useWorkspaceJobsStore } from "@/stores/workspace-jobs-store"
import i18n from "@/i18n"

type LocationSelectionSnapshot = {
  locationId: number | null
  variantId: number | null
  facing: LocationFacing
  viewpointId: number | null
}

type Props = {
  projectId: number
  shotId: number
  onClose: () => void
  onDataChanged: () => void
  onInitialLoadSettled?: () => void
}

function facingPrereqMessage(reason: LocationFacingPrereqReason): string {
  return i18n.t(`locationsPage.facingPrereq.${reason}`, { ns: "workspace" })
}

export function ShotLocationView({ projectId, shotId, onClose, onDataChanged, onInitialLoadSettled }: Props) {
  const { t } = useTranslation(["shot", "common", "workspace"])
  const { project, generation_models_by_context } = useShotPanelWorkspace()
  const jobs = useWorkspaceJobsStore((s) => s.jobs)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)
  const reportInitialLoad = useInitialLoadReporter(onInitialLoadSettled)

  const [projectLocations, setProjectLocations] = useState<ShotLocationPickerRow[]>([])

  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null)
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null)
  const [selectedFacing, setSelectedFacing] = useState<LocationFacing>(PRIMARY_SHOT_LOCATION_FACING)
  const [selectedViewpointId, setSelectedViewpointId] = useState<number | null>(null)

  const [roomViewerOpen, setRoomViewerOpen] = useState(false)
  const [creatingViewpoint, setCreatingViewpoint] = useState(false)
  const [panoramaGalleryOpen, setPanoramaGalleryOpen] = useState(false)
  const [panoramaCorrection, setPanoramaCorrection] = useState<{
    version: LocationReferenceVersionRow
  } | null>(null)

  const refreshLocations = useCallback(async () => {
    const locations = (await fetchProjectLocations(projectId)) as ShotLocationPickerRow[]
    setProjectLocations(locations)

    setSelectedViewpointId((prev) => {
      if (prev == null) return null
      const loc = locations.find((l) => l.id === selectedLocationId)
      const variant = loc?.variants.find((v) => v.id === selectedVariantId)
      const byId = new Map<number, LocationViewpointRow>()
      for (const vp of [...(loc?.viewpoints ?? []), ...(variant?.viewpoints ?? [])]) byId.set(vp.id, vp)
      return byId.has(prev) ? prev : null
    })
  }, [projectId, selectedLocationId, selectedVariantId])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    Promise.all([fetchShotWorkspaceDetail(projectId, shotId), fetchProjectLocations(projectId)])
      .then(([detail, locations]) => {
        if (cancelled) return
        const shot = detail.shot
        const variantId = shot.location_variant_id ?? null
        const facing = shot.location_facing ?? null
        const viewpointId = shot.location_viewpoint_id ?? null
        const rows = locations as ShotLocationPickerRow[]

        setProjectLocations(rows)

        const loc = findLocationByVariantId(rows, variantId)
        const mergedViewpoints = (() => {
          const variant = loc?.variants.find((v) => v.id === variantId)
          const byId = new Map<number, LocationViewpointRow>()
          for (const vp of [...(loc?.viewpoints ?? []), ...(variant?.viewpoints ?? [])]) byId.set(vp.id, vp)
          return byId
        })()

        setSelectedLocationId(loc?.id ?? null)
        setSelectedVariantId(variantId)
        setSelectedFacing(normalizeShotLocationFacing(facing))
        setSelectedViewpointId(viewpointId != null && mergedViewpoints.has(viewpointId) ? viewpointId : null)
        setLoading(false)
        reportInitialLoad()
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : t("location.loadError"))
        setLoading(false)
        reportInitialLoad()
      })

    return () => {
      cancelled = true
    }
  }, [projectId, shotId, t, reportInitialLoad])

  const selectedLocation = useMemo(
    () => projectLocations.find((l) => l.id === selectedLocationId) ?? null,
    [projectLocations, selectedLocationId],
  )

  const selectedVariant = useMemo(() => {
    if (selectedVariantId == null || !selectedLocation) return null
    return selectedLocation.variants.find((v) => v.id === selectedVariantId) ?? null
  }, [selectedLocation, selectedVariantId])

  const viewingDefaultVariant = Boolean(selectedVariant?.is_default)
  const roomVariantId = viewingDefaultVariant ? null : selectedVariantId
  const roomUrlsByFacing = selectedVariant?.reference_urls_by_facing
  const roomPrimaryFallback = selectedVariant?.reference_url ?? selectedLocation?.reference_sheet_url ?? null
  const roomViewpoints = viewingDefaultVariant
    ? (selectedLocation?.viewpoints ?? [])
    : (selectedVariant?.viewpoints ?? [])

  const baseReferenceMissing =
    !selectedLocation?.reference_sheet_url &&
    !Object.values(selectedLocation?.reference_urls_by_facing ?? {}).some((u) => typeof u === "string" && u.length > 0)

  const roomDownloadUrlsByFacing = selectedVariant?.reference_download_urls_by_facing
  const roomWallUrls = useMemo(
    () => ({
      north: roomDownloadUrlsByFacing?.north ?? roomUrlsByFacing?.north ?? null,
      east: roomDownloadUrlsByFacing?.east ?? roomUrlsByFacing?.east ?? null,
      south: roomDownloadUrlsByFacing?.south ?? roomUrlsByFacing?.south ?? null,
      west: roomDownloadUrlsByFacing?.west ?? roomUrlsByFacing?.west ?? null,
    }),
    [
      roomDownloadUrlsByFacing?.north,
      roomDownloadUrlsByFacing?.east,
      roomDownloadUrlsByFacing?.south,
      roomDownloadUrlsByFacing?.west,
      roomUrlsByFacing?.north,
      roomUrlsByFacing?.east,
      roomUrlsByFacing?.south,
      roomUrlsByFacing?.west,
    ],
  )

  const roomWallBusy = useMemo(() => {
    const out: Partial<Record<LocationCardinalFacing, boolean>> = {}
    if (selectedLocationId == null) return out
    for (const f of LOCATION_CARDINAL_FACINGS) {
      out[f] =
        roomVariantId != null
          ? variantReferenceBusy(jobs, selectedLocationId, roomVariantId, f)
          : locationReferenceBusy(jobs, selectedLocationId, f)
    }
    return out
  }, [jobs, selectedLocationId, roomVariantId])

  const roomPanoramaUrl = roomUrlsByFacing?.panorama ?? null
  const roomPanoramaBusy =
    selectedLocationId == null
      ? false
      : roomVariantId != null
        ? variantReferenceBusy(jobs, selectedLocationId, roomVariantId, LOCATION_PANORAMA_FACING)
        : locationReferenceBusy(jobs, selectedLocationId, LOCATION_PANORAMA_FACING)

  const roomPanoramaVersionCount = viewingDefaultVariant
    ? (selectedLocation?.reference_version_counts_by_facing?.[LOCATION_PANORAMA_FACING] ?? 0)
    : (selectedVariant?.reference_version_counts_by_facing?.[LOCATION_PANORAMA_FACING] ?? 0)

  const panoramaGenerateBlockedReason = useCallback(() => {
    if (selectedLocationId == null || selectedVariant == null) return t("location.facingHint")
    const reason = panoramaGenerationBlockReason(
      roomUrlsByFacing,
      roomPrimaryFallback,
      selectedLocation?.reference_urls_by_facing,
      selectedLocation?.reference_sheet_url,
    )
    return reason ? facingPrereqMessage(reason) : null
  }, [
    selectedLocationId,
    selectedVariant,
    selectedLocation?.reference_urls_by_facing,
    selectedLocation?.reference_sheet_url,
    roomUrlsByFacing,
    roomPrimaryFallback,
    t,
  ])

  const activeLocationReferenceJob = useMemo(() => {
    if (selectedLocationId == null) return null
    return (
      jobs.find(
        (j) =>
          (j.status === "queued" || j.status === "processing") &&
          j.job_kind === "location_reference" &&
          Number(j.location_id) === selectedLocationId &&
          (roomVariantId == null
            ? (j.location_variant_id ?? null) === null
            : Number(j.location_variant_id) === roomVariantId),
      ) ?? null
    )
  }, [jobs, selectedLocationId, roomVariantId])

  useReloadOnJobFinish(activeLocationReferenceJob, () => {
    void refreshLocations()
  })

  const modelLabel = useMemo(() => {
    const key = project?.effective_generation_model_keys?.location_reference
    if (!key) return null
    const entry = generation_models_by_context?.location_reference?.find((m) => m.id === key)
    return entry?.label ?? key
  }, [project?.effective_generation_model_keys?.location_reference, generation_models_by_context?.location_reference])

  const wallGenerateBlockedReason = useCallback(
    (wallFacing: LocationCardinalFacing) => {
      if (selectedLocationId == null || selectedVariant == null) return t("location.facingHint")
      if (!viewingDefaultVariant) {
        if (baseReferenceMissing) return t("locationsPage.variantNeedBaseHint", { ns: "workspace" })
        const reason = facingGenerationBlockReason(wallFacing, roomUrlsByFacing, roomPrimaryFallback)
        return reason ? facingPrereqMessage(reason) : null
      }
      const reason = facingGenerationBlockReason(wallFacing, roomUrlsByFacing, roomPrimaryFallback)
      return reason ? facingPrereqMessage(reason) : null
    },
    [
      selectedLocationId,
      selectedVariant,
      viewingDefaultVariant,
      roomUrlsByFacing,
      roomPrimaryFallback,
      baseReferenceMissing,
      t,
    ],
  )

  const restoreSelection = (snapshot: LocationSelectionSnapshot) => {
    setSelectedLocationId(snapshot.locationId)
    setSelectedVariantId(snapshot.variantId)
    setSelectedFacing(snapshot.facing)
    setSelectedViewpointId(snapshot.viewpointId)
  }

  const persist = (
    variantId: number | null,
    facing: LocationFacing,
    viewpointId: number | null,
    snapshot: LocationSelectionSnapshot,
  ) => {
    if (savingRef.current) return
    savingRef.current = true
    setSaving(true)
    const visitOptions = {
      onSuccess: () => toast.success(t("location.saved")),
      onError: () => {
        toast.error(t("location.saveFailed"))
        restoreSelection(snapshot)
      },
      onFinish: () => {
        savingRef.current = false
        setSaving(false)
        onDataChanged()
      },
    }
    if (variantId == null) {
      patchShotLocationVariant(
        projectId,
        shotId,
        { location_variant_id: null, location_viewpoint_id: null },
        visitOptions,
      )
    } else {
      patchShotLocationVariant(
        projectId,
        shotId,
        {
          location_variant_id: variantId,
          location_facing: facing,
          location_viewpoint_id: viewpointId,
        },
        visitOptions,
      )
    }
  }

  const selectionSnapshot = (): LocationSelectionSnapshot => ({
    locationId: selectedLocationId,
    variantId: selectedVariantId,
    facing: selectedFacing,
    viewpointId: selectedViewpointId,
  })

  const toggleLocation = (locId: number) => {
    if (saving) return
    const snapshot = selectionSnapshot()
    if (selectedLocationId === locId) {
      setSelectedLocationId(null)
      setSelectedVariantId(null)
      setSelectedFacing(PRIMARY_SHOT_LOCATION_FACING)
      setSelectedViewpointId(null)
      setRoomViewerOpen(false)
      persist(null, PRIMARY_SHOT_LOCATION_FACING, null, snapshot)
    } else {
      setSelectedLocationId(locId)
      const loc = projectLocations.find((l) => l.id === locId)
      const firstVariantId = loc?.variants[0]?.id ?? null
      setSelectedVariantId(firstVariantId)
      setSelectedFacing(PRIMARY_SHOT_LOCATION_FACING)
      setSelectedViewpointId(null)
      persist(firstVariantId, PRIMARY_SHOT_LOCATION_FACING, null, snapshot)
    }
  }

  const selectVariant = (variantId: number) => {
    if (saving) return
    const snapshot = selectionSnapshot()
    setSelectedVariantId(variantId)
    setSelectedViewpointId(null)
    persist(variantId, selectedFacing, null, snapshot)
  }

  const selectFacing = (facing: LocationFacing) => {
    if (saving) return
    const snapshot = selectionSnapshot()
    setSelectedFacing(facing)
    setSelectedViewpointId(null)
    persist(selectedVariantId, facing, null, snapshot)
  }

  const selectViewpoint = (vp: LocationViewpointRow) => {
    if (saving) return
    const snapshot = selectionSnapshot()
    setSelectedViewpointId(vp.id)
    persist(selectedVariantId, selectedFacing, vp.id, snapshot)
  }

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-ui-text-muted" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 py-20 text-sm text-ui-status-failed">
        <p>{error}</p>
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          {t("common.close")}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex max-h-full flex-col">
      <div className="shrink-0 px-4 pb-2">
        <h2 className="text-sm font-semibold text-ui-text-primary">{t("location.title")}</h2>
        <p className="mt-0.5 text-xs text-ui-text-muted">{t("location.hint")}</p>
      </div>
      <ShotLocationPicker
        projectId={projectId}
        projectLocations={projectLocations}
        selectedLocationId={selectedLocationId}
        selectedVariantId={selectedVariantId}
        selectedFacing={selectedFacing}
        selectedViewpointId={selectedViewpointId}
        onToggleLocation={toggleLocation}
        onSelectVariant={selectVariant}
        onSelectFacing={selectFacing}
        onSelectViewpoint={selectViewpoint}
        onOpenRoomViewer={selectedVariantId != null ? () => setRoomViewerOpen(true) : undefined}
        enableImagePreview
        searchClassName="px-4 py-2.5"
        listClassName="p-4"
        anglesClassName="px-4 pt-3 pb-3"
      />

      {selectedLocation && selectedVariant ? (
        <LocationRoomViewerDialog
          open={roomViewerOpen}
          onOpenChange={setRoomViewerOpen}
          locationName={selectedLocation.name}
          variantName={viewingDefaultVariant ? null : selectedVariant.display_name}
          wallUrls={roomWallUrls}
          wallBusy={roomWallBusy}
          modelLabel={modelLabel}
          onGenerateWall={(wallFacing) => {
            const blocked = wallGenerateBlockedReason(wallFacing)
            if (blocked) {
              toast.error(blocked)
              return
            }
            void generateLocationReference(projectId, selectedLocation.id, roomVariantId, { facing: wallFacing }).catch(
              (e) => toast.error(e instanceof Error ? e.message : String(e)),
            )
          }}
          wallGenerateBlockedReason={wallGenerateBlockedReason}
          confirmDelayKeyForWall={(facing) =>
            roomVariantId != null
              ? confirmDelayKey("location", selectedLocation.id, "variant", roomVariantId, facing)
              : confirmDelayKey("location", selectedLocation.id, facing)
          }
          panoramaUrl={roomPanoramaUrl}
          panoramaBusy={roomPanoramaBusy}
          panoramaGenerateBlockedReason={panoramaGenerateBlockedReason()}
          confirmDelayKeyForPanorama={
            roomVariantId != null
              ? confirmDelayKey("location", selectedLocation.id, "variant", roomVariantId, LOCATION_PANORAMA_FACING)
              : confirmDelayKey("location", selectedLocation.id, LOCATION_PANORAMA_FACING)
          }
          onGeneratePanorama={() => {
            const blocked = panoramaGenerateBlockedReason()
            if (blocked) {
              toast.error(blocked)
              return
            }
            void generateLocationReference(projectId, selectedLocation.id, roomVariantId, {
              facing: LOCATION_PANORAMA_FACING,
            }).catch((e) => toast.error(e instanceof Error ? e.message : String(e)))
          }}
          panoramaVersionCount={roomPanoramaVersionCount}
          onOpenPanoramaVersions={() => setPanoramaGalleryOpen(true)}
          viewpoints={roomViewpoints}
          creatingViewpoint={creatingViewpoint}
          onCreateViewpoint={async (blob, cameraMeta) => {
            setCreatingViewpoint(true)
            try {
              await addLocationViewpoint(projectId, selectedLocation.id, blob, cameraMeta, roomVariantId)
              await refreshLocations()
            } catch (e) {
              toast.error(e instanceof Error ? e.message : String(e))
            } finally {
              setCreatingViewpoint(false)
            }
          }}
          onRemoveViewpoint={async (viewpointId) => {
            try {
              const wasSelected = selectedViewpointId === viewpointId
              await removeLocationViewpoint(projectId, selectedLocation.id, viewpointId)
              await refreshLocations()
              if (wasSelected) {
                setSelectedViewpointId(null)
                onDataChanged()
              }
            } catch (e) {
              toast.error(e instanceof Error ? e.message : String(e))
            }
          }}
          onRenameViewpoint={async (viewpointId, name) => {
            try {
              await updateLocationViewpoint(projectId, selectedLocation.id, viewpointId, { name })
              await refreshLocations()
            } catch (e) {
              toast.error(e instanceof Error ? e.message : String(e))
            }
          }}
        />
      ) : null}

      {selectedLocation ? (
        <LocationReferencesGallery
          open={panoramaGalleryOpen}
          onOpenChange={setPanoramaGalleryOpen}
          projectId={projectId}
          locationId={selectedLocation.id}
          locationName={selectedLocation.name}
          variantId={roomVariantId}
          variantName={viewingDefaultVariant ? null : selectedVariant?.display_name}
          facing={LOCATION_PANORAMA_FACING}
          allowUpload={false}
          onChanged={() => void refreshLocations()}
          onCorrect={(v) => {
            setPanoramaGalleryOpen(false)
            setPanoramaCorrection({ version: v })
          }}
        />
      ) : null}

      {selectedLocation && panoramaCorrection?.version.preview_url ? (
        <LocationCorrectionDialog
          open={!!panoramaCorrection}
          onOpenChange={(o) => {
            if (!o) setPanoramaCorrection(null)
          }}
          projectId={projectId}
          locationId={selectedLocation.id}
          variantId={roomVariantId}
          versionId={panoramaCorrection.version.id}
          versionImageUrl={panoramaCorrection.version.preview_url}
          facing={panoramaCorrection.version.facing}
          onSubmitted={() => {
            setPanoramaCorrection(null)
            void refreshLocations()
          }}
        />
      ) : null}
    </div>
  )
}
